export type LinkEmbed = {
  provider: "drive" | "youtube" | "canva";
  src: string;
};

// Turns a shared link into the provider's embeddable player, so a piece
// stored in Drive, YouTube or Canva shows inside the post preview. Returns
// null for any other link (it stays as an "open link" button).
export const getLinkEmbed = (rawUrl: string): LinkEmbed | null => {
  let url: URL;
  try {
    url = new URL(rawUrl.trim());
  } catch {
    return null;
  }
  const host = url.hostname.replace(/^www\./, "");

  if (host === "drive.google.com" || host === "docs.google.com") {
    const folder = url.pathname.match(/\/folders\/([\w-]+)/);
    if (folder) {
      return {
        provider: "drive",
        src: `https://drive.google.com/embeddedfolderview?id=${folder[1]}#grid`,
      };
    }
    const fileId =
      url.pathname.match(
        /\/(?:file|presentation|document)\/d\/([\w-]+)/,
      )?.[1] ?? url.searchParams.get("id");
    return fileId
      ? {
          provider: "drive",
          src: `https://drive.google.com/file/d/${fileId}/preview`,
        }
      : null;
  }

  if (host === "youtu.be" || host.endsWith("youtube.com")) {
    const videoId =
      host === "youtu.be"
        ? url.pathname.slice(1).split("/")[0]
        : (url.searchParams.get("v") ??
          url.pathname.match(/\/(?:shorts|embed|live)\/([\w-]+)/)?.[1]);
    return videoId
      ? {
          provider: "youtube",
          src: `https://www.youtube-nocookie.com/embed/${videoId}?rel=0`,
        }
      : null;
  }

  if (host === "canva.com" || host.endsWith(".canva.com")) {
    // /design/{id}/{token?}/view|edit -> /design/{id}/{token?}/view?embed
    const design = url.pathname.match(/\/design\/([\w-]+)(?:\/([\w-]+))?/);
    if (!design) return null;
    const token =
      design[2] && !["view", "edit", "watch"].includes(design[2])
        ? `/${design[2]}`
        : "";
    return {
      provider: "canva",
      src: `https://www.canva.com/design/${design[1]}${token}/view?embed`,
    };
  }

  return null;
};
